/**
 * EnrolWizard — public multi-step ClientOps onboarding funnel.
 *
 * Steps: 1) Basics 2) Service details 3) Authority to Act 4) Payment (Stripe).
 * Called by /enrol/sole-trader and /enrol/company. All state lives client-side
 * plus a server-side draft row keyed by `enrolment_id` so a browser refresh
 * mid-funnel doesn't wipe progress (the id is stashed in localStorage).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const gst = (base) => (base * 1.15).toFixed(2);

const STEPS = ["basics", "service", "authority"];

function StepDots({ current }) {
    return (
        <div className="flex items-center gap-2" data-testid="enrol-step-dots">
            {STEPS.map((s, i) => {
                const active = STEPS.indexOf(current) >= i;
                return (
                    <div key={s} className="flex items-center gap-2">
                        <span
                            className={`h-2 w-2 rounded-full ${active ? "bg-[#0F1115]" : "bg-[#D4D4D8]"}`}
                            data-testid={`enrol-dot-${s}`}
                        />
                        {i < STEPS.length - 1 && <span className="h-px w-6 bg-[#D4D4D8]" />}
                    </div>
                );
            })}
        </div>
    );
}

function FieldLabel({ children }) {
    return <label className="block text-[11px] font-medium uppercase tracking-wide text-[#5E636E] mb-1">{children}</label>;
}

const inputCx = "w-full rounded-md border border-[#D4D4D8] bg-white px-3 py-2 text-[14px] text-[#0F1115] focus:outline-none focus:ring-1 focus:ring-[#0F1115] focus:border-[#0F1115]";

export default function EnrolWizard({ service, plan }) {
    const [enrolmentId, setEnrolmentId] = useState(null);
    const [step, setStep] = useState("basics");
    const [busy, setBusy] = useState(false);
    const [duplicateEmail, setDuplicateEmail] = useState(false);

    // Step 1 fields
    const [basics, setBasics] = useState({
        full_name: "", email: "", mobile: "", password: "",
        ird_number: "", date_of_birth: "", residential_address: "",
    });

    // Step 2 fields — shape depends on service
    const [soleTrader, setSoleTrader] = useState({
        business_name: "", business_nature: "", est_annual_turnover: "$0-30k",
        gst_registered: false, wants_gst_registration: false, uses_xero: false,
        transferring_from_accountant: false,
        previous_accountant_name: "", previous_accountant_email: "",
    });
    const [company, setCompany] = useState({
        company_name: "", nzbn: "", company_number: "", gst_number: "",
        gst_registered: true, wants_gst_registration: false, industry: "",
        shareholders: [
            { full_name: "", email: "", ird_number: "", ownership_percent: 50 },
            { full_name: "", email: "", ird_number: "", ownership_percent: 50 },
        ],
        transferring_from_accountant: false,
        previous_accountant_name: "", previous_accountant_email: "",
    });

    // Step 3
    const [typedName, setTypedName] = useState("");
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [authoriseEfiling, setAuthoriseEfiling] = useState(true);
    const signatureRef = useRef(null);

    // Rehydrate any in-flight enrolment.
    useEffect(() => {
        const key = `enrol_id:${service}`;
        const saved = localStorage.getItem(key);
        if (saved) setEnrolmentId(saved);
    }, [service]);

    async function ensureEnrolment() {
        if (enrolmentId) return enrolmentId;
        const source = new URLSearchParams(window.location.search).get("src");
        const { data } = await axios.post(`${API_URL}/api/enrol/start`, {
            service, source: source || null,
        });
        localStorage.setItem(`enrol_id:${service}`, data.enrolment_id);
        setEnrolmentId(data.enrolment_id);
        return data.enrolment_id;
    }

    async function submitBasics(e) {
        e.preventDefault();
        setBusy(true); setDuplicateEmail(false);
        try {
            const id = await ensureEnrolment();
            await axios.patch(`${API_URL}/api/enrol/${id}/basics`, basics);
            setStep("service");
        } catch (err) {
            if (err?.response?.status === 409) {
                setDuplicateEmail(true);
            } else {
                toast.error(err?.response?.data?.detail || "Could not save your details");
            }
        } finally {
            setBusy(false);
        }
    }

    async function submitService(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const id = enrolmentId || await ensureEnrolment();
            const payload = service === "sole_trader" ? soleTrader : company;
            await axios.patch(`${API_URL}/api/enrol/${id}/service`, payload);
            setStep("authority");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save these details");
        } finally { setBusy(false); }
    }

    async function submitAuthority(e) {
        e.preventDefault();
        if (!signatureRef.current || signatureRef.current.isEmpty()) {
            toast.error("Please draw your signature to sign the Authority to Act");
            return;
        }
        setBusy(true);
        try {
            const id = enrolmentId;
            const signature = signatureRef.current.toDataURL();
            await axios.post(`${API_URL}/api/enrol/${id}/authority-to-act`, {
                typed_name: typedName || basics.full_name,
                accept_terms: acceptTerms,
                authorise_efiling: authoriseEfiling,
                signature_base64: signature,
            });
            await axios.post(`${API_URL}/api/enrol/${id}/complete`);
            window.location.href = `/enrol/success?enrolment_id=${id}`;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not sign the authority");
            setBusy(false);
        }
    }

    const priceInclLine = useMemo(
        () => `NZ$${plan.base_nzd} + GST = NZ$${gst(plan.base_nzd)} / month`,
        [plan.base_nzd],
    );

    return (
        <div className="bg-white rounded-2xl border border-[#E4E4E7] shadow-sm p-6 md:p-8" data-testid="enrol-wizard">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">{plan.label}</div>
                    <div className="text-[14px] font-medium text-[#0F1115]">{priceInclLine}</div>
                </div>
                <StepDots current={step} />
            </div>

            {step === "basics" && (
                <form onSubmit={submitBasics} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enrol-basics-form">
                    <div className="md:col-span-2">
                        <FieldLabel>Full legal name</FieldLabel>
                        <input required className={inputCx} value={basics.full_name}
                            onChange={(e) => setBasics({ ...basics, full_name: e.target.value })}
                            data-testid="enrol-basics-name" />
                    </div>
                    <div>
                        <FieldLabel>Email</FieldLabel>
                        <input required type="email" className={inputCx} value={basics.email}
                            onChange={(e) => setBasics({ ...basics, email: e.target.value })}
                            data-testid="enrol-basics-email" />
                    </div>
                    <div>
                        <FieldLabel>Mobile</FieldLabel>
                        <input required className={inputCx} value={basics.mobile}
                            onChange={(e) => setBasics({ ...basics, mobile: e.target.value })}
                            data-testid="enrol-basics-mobile" />
                    </div>
                    <div>
                        <FieldLabel>Password (for ClientOps login)</FieldLabel>
                        <input required type="password" minLength={8} className={inputCx} value={basics.password}
                            onChange={(e) => setBasics({ ...basics, password: e.target.value })}
                            data-testid="enrol-basics-password" />
                    </div>
                    <div>
                        <FieldLabel>IRD number <span className="text-[#5E636E]">(optional now — we'll ask later)</span></FieldLabel>
                        <input className={inputCx} value={basics.ird_number}
                            onChange={(e) => setBasics({ ...basics, ird_number: e.target.value })}
                            data-testid="enrol-basics-ird" />
                    </div>
                    <div>
                        <FieldLabel>Date of birth</FieldLabel>
                        <input type="date" className={inputCx} value={basics.date_of_birth}
                            onChange={(e) => setBasics({ ...basics, date_of_birth: e.target.value })}
                            data-testid="enrol-basics-dob" />
                    </div>
                    <div className="md:col-span-2">
                        <FieldLabel>Residential address</FieldLabel>
                        <input className={inputCx} value={basics.residential_address}
                            onChange={(e) => setBasics({ ...basics, residential_address: e.target.value })}
                            data-testid="enrol-basics-address" />
                    </div>

                    {duplicateEmail && (
                        <div className="md:col-span-2 rounded-md bg-[#FEF7ED] border border-[#F59E0B] px-3 py-2 text-[13px] text-[#78350F]" data-testid="enrol-duplicate-banner">
                            You already have a ClientOps account with this email.{" "}
                            <a href="/login" className="underline font-medium">Sign in instead</a>.
                        </div>
                    )}

                    <div className="md:col-span-2 flex justify-end pt-2">
                        <button type="submit" disabled={busy}
                            className="rounded-full bg-[#0F1115] text-white px-6 py-2.5 text-[14px] font-medium hover:bg-[#1F2328] disabled:opacity-50"
                            data-testid="enrol-basics-next">
                            {busy ? "Saving…" : "Continue →"}
                        </button>
                    </div>
                </form>
            )}

            {step === "service" && service === "sole_trader" && (
                <form onSubmit={submitService} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enrol-service-sole-trader">
                    <div>
                        <FieldLabel>Business name <span className="text-[#5E636E]">(optional — use your own name if you don't trade under one)</span></FieldLabel>
                        <input className={inputCx} value={soleTrader.business_name}
                            onChange={(e) => setSoleTrader({ ...soleTrader, business_name: e.target.value })}
                            data-testid="enrol-st-biz-name" />
                    </div>
                    <div>
                        <FieldLabel>Industry or type of work you do (e.g. Carpenter, Labour Services)</FieldLabel>
                        <input required className={inputCx} value={soleTrader.business_nature}
                            onChange={(e) => setSoleTrader({ ...soleTrader, business_nature: e.target.value })}
                            data-testid="enrol-st-nature" />
                    </div>
                    <div>
                        <FieldLabel>Estimated annual turnover</FieldLabel>
                        <select className={inputCx} value={soleTrader.est_annual_turnover}
                            onChange={(e) => setSoleTrader({ ...soleTrader, est_annual_turnover: e.target.value })}
                            data-testid="enrol-st-turnover">
                            <option>$0-30k</option>
                            <option>$30k-60k</option>
                            <option>$60k-100k</option>
                            <option>$100k+</option>
                        </select>
                    </div>
                    <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={soleTrader.gst_registered}
                                onChange={(e) => setSoleTrader({ ...soleTrader, gst_registered: e.target.checked })}
                                data-testid="enrol-st-gst" />
                            I'm already GST-registered
                        </label>
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={soleTrader.wants_gst_registration}
                                onChange={(e) => setSoleTrader({ ...soleTrader, wants_gst_registration: e.target.checked })}
                                data-testid="enrol-st-wants-gst" />
                            Please register me for GST
                        </label>
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={soleTrader.uses_xero}
                                onChange={(e) => setSoleTrader({ ...soleTrader, uses_xero: e.target.checked })}
                                data-testid="enrol-st-xero" />
                            I use Xero
                        </label>
                    </div>

                    <div className="md:col-span-2 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4">
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={soleTrader.transferring_from_accountant}
                                onChange={(e) => setSoleTrader({ ...soleTrader, transferring_from_accountant: e.target.checked })}
                                data-testid="enrol-st-transfer" />
                            <span>Transferring from another accountant?</span>
                        </label>
                        {soleTrader.transferring_from_accountant && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Previous accountant name</FieldLabel>
                                    <input className={inputCx} value={soleTrader.previous_accountant_name}
                                        onChange={(e) => setSoleTrader({ ...soleTrader, previous_accountant_name: e.target.value })}
                                        data-testid="enrol-st-prev-acc-name" />
                                </div>
                                <div>
                                    <FieldLabel>Previous accountant email</FieldLabel>
                                    <input type="email" className={inputCx} value={soleTrader.previous_accountant_email}
                                        onChange={(e) => setSoleTrader({ ...soleTrader, previous_accountant_email: e.target.value })}
                                        data-testid="enrol-st-prev-acc-email" />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="md:col-span-2 flex justify-between pt-2">
                        <button type="button" onClick={() => setStep("basics")}
                            className="text-[13px] text-[#5E636E] hover:text-[#0F1115]"
                            data-testid="enrol-service-back">← Back</button>
                        <button type="submit" disabled={busy}
                            className="rounded-full bg-[#0F1115] text-white px-6 py-2.5 text-[14px] font-medium hover:bg-[#1F2328] disabled:opacity-50"
                            data-testid="enrol-service-next">
                            {busy ? "Saving…" : "Continue →"}
                        </button>
                    </div>
                </form>
            )}

            {step === "service" && service === "company_2owner" && (
                <form onSubmit={submitService} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enrol-service-company">
                    <div className="md:col-span-2">
                        <FieldLabel>Company name</FieldLabel>
                        <input required className={inputCx} value={company.company_name}
                            onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                            data-testid="enrol-co-name" />
                    </div>
                    <div>
                        <FieldLabel>NZBN</FieldLabel>
                        <input className={inputCx} value={company.nzbn}
                            onChange={(e) => setCompany({ ...company, nzbn: e.target.value })}
                            data-testid="enrol-co-nzbn" />
                    </div>
                    <div>
                        <FieldLabel>Company number</FieldLabel>
                        <input className={inputCx} value={company.company_number}
                            onChange={(e) => setCompany({ ...company, company_number: e.target.value })}
                            data-testid="enrol-co-number" />
                    </div>
                    <div>
                        <FieldLabel>GST number (if registered)</FieldLabel>
                        <input className={inputCx} value={company.gst_number}
                            onChange={(e) => setCompany({ ...company, gst_number: e.target.value })}
                            data-testid="enrol-co-gst-number" />
                    </div>
                    <div>
                        <FieldLabel>Industry</FieldLabel>
                        <input className={inputCx} value={company.industry}
                            onChange={(e) => setCompany({ ...company, industry: e.target.value })}
                            data-testid="enrol-co-industry" />
                    </div>
                    <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={company.gst_registered}
                                onChange={(e) => setCompany({ ...company, gst_registered: e.target.checked })}
                                data-testid="enrol-co-gst-toggle" />
                            Company is GST-registered
                        </label>
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={company.wants_gst_registration}
                                onChange={(e) => setCompany({ ...company, wants_gst_registration: e.target.checked })}
                                data-testid="enrol-co-wants-gst" />
                            Please register us for GST
                        </label>
                    </div>

                    <div className="md:col-span-2 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4">
                        <label className="inline-flex items-center gap-2 text-[13px] text-[#0F1115]">
                            <input type="checkbox" checked={company.transferring_from_accountant}
                                onChange={(e) => setCompany({ ...company, transferring_from_accountant: e.target.checked })}
                                data-testid="enrol-co-transfer" />
                            <span>Transferring from another accountant?</span>
                        </label>
                        {company.transferring_from_accountant && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Previous accountant name</FieldLabel>
                                    <input className={inputCx} value={company.previous_accountant_name}
                                        onChange={(e) => setCompany({ ...company, previous_accountant_name: e.target.value })}
                                        data-testid="enrol-co-prev-acc-name" />
                                </div>
                                <div>
                                    <FieldLabel>Previous accountant email</FieldLabel>
                                    <input type="email" className={inputCx} value={company.previous_accountant_email}
                                        onChange={(e) => setCompany({ ...company, previous_accountant_email: e.target.value })}
                                        data-testid="enrol-co-prev-acc-email" />
                                </div>
                            </div>
                        )}
                    </div>

                    {[0, 1].map((idx) => (
                        <div key={idx} className="md:col-span-2 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4">
                            <div className="text-[12px] uppercase tracking-wide text-[#5E636E] mb-3">Shareholder {idx + 1}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Full name</FieldLabel>
                                    <input required className={inputCx} value={company.shareholders[idx].full_name}
                                        onChange={(e) => {
                                            const s = [...company.shareholders];
                                            s[idx] = { ...s[idx], full_name: e.target.value };
                                            setCompany({ ...company, shareholders: s });
                                        }}
                                        data-testid={`enrol-co-shareholder-${idx}-name`} />
                                </div>
                                <div>
                                    <FieldLabel>Email</FieldLabel>
                                    <input type="email" className={inputCx} value={company.shareholders[idx].email}
                                        onChange={(e) => {
                                            const s = [...company.shareholders];
                                            s[idx] = { ...s[idx], email: e.target.value };
                                            setCompany({ ...company, shareholders: s });
                                        }}
                                        data-testid={`enrol-co-shareholder-${idx}-email`} />
                                </div>
                                <div>
                                    <FieldLabel>IRD number</FieldLabel>
                                    <input className={inputCx} value={company.shareholders[idx].ird_number}
                                        onChange={(e) => {
                                            const s = [...company.shareholders];
                                            s[idx] = { ...s[idx], ird_number: e.target.value };
                                            setCompany({ ...company, shareholders: s });
                                        }}
                                        data-testid={`enrol-co-shareholder-${idx}-ird`} />
                                </div>
                                <div>
                                    <FieldLabel>Ownership %</FieldLabel>
                                    <input type="number" min="0" max="100" step="0.01" className={inputCx}
                                        value={company.shareholders[idx].ownership_percent}
                                        onChange={(e) => {
                                            const s = [...company.shareholders];
                                            s[idx] = { ...s[idx], ownership_percent: parseFloat(e.target.value) };
                                            setCompany({ ...company, shareholders: s });
                                        }}
                                        data-testid={`enrol-co-shareholder-${idx}-ownership`} />
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="md:col-span-2 flex justify-between pt-2">
                        <button type="button" onClick={() => setStep("basics")}
                            className="text-[13px] text-[#5E636E] hover:text-[#0F1115]"
                            data-testid="enrol-service-back">← Back</button>
                        <button type="submit" disabled={busy}
                            className="rounded-full bg-[#0F1115] text-white px-6 py-2.5 text-[14px] font-medium hover:bg-[#1F2328] disabled:opacity-50"
                            data-testid="enrol-service-next">
                            {busy ? "Saving…" : "Continue →"}
                        </button>
                    </div>
                </form>
            )}

            {step === "authority" && (
                <form onSubmit={submitAuthority} className="space-y-4" data-testid="enrol-authority-form">
                    <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4 text-[13px] leading-6 text-[#1F2328] max-h-72 overflow-y-auto">
                        <div className="font-medium text-[#0F1115] mb-1">NZ Taxation Limited t/a ClientOps</div>
                        <div className="text-[11px] text-[#5E636E] mb-3">785A Te Atatu Road, Te Atatu Peninsula, Auckland 0610 · info@nztaxation.nz</div>
                        <div className="font-medium text-[#0F1115]">Authority to Act as Tax Agent — Engagement / Contract</div>
                        <p className="mt-2 text-[12px] italic text-[#5E636E]">(All information treated with strict confidentiality)</p>
                        <p className="mt-2">I hereby authorise <b>NZ Taxation Limited</b> to act as my tax agent for the purpose of preparing and filing my tax returns with Inland Revenue, based on the information I provide.</p>
                        <p className="mt-2">I also grant permission for NZ Taxation Limited and its staff to communicate with Inland Revenue on my behalf regarding my tax matters. This includes the authority to access, provide, and obtain information related to all current and future revenue obligations.</p>
                        <p className="mt-2">I understand that such information may be exchanged through telephone, written correspondence, online services, or any other appropriate means.</p>
                        <p className="mt-3 text-[11px] text-[#5E636E]">As a client of an agent you receive an extension of time to file your income tax returns. If you do not give us records in time to prepare the returns by due date you will receive a letter from Inland Revenue outlining your obligations. You may be charged a late filing penalty. If you are charged a late filing penalty, you lose your extension of time privilege.</p>
                    </div>

                    <label className="inline-flex items-start gap-2 text-[13px] text-[#0F1115]" data-testid="enrol-authority-efile-wrap">
                        <input type="checkbox" checked={authoriseEfiling}
                            onChange={(e) => setAuthoriseEfiling(e.target.checked)}
                            className="mt-0.5"
                            data-testid="enrol-authority-authorise-efiling" />
                        <span>I also authorise NZ Taxation Limited to <b>sign and submit tax returns to Inland Revenue on my behalf</b>.</span>
                    </label>

                    <div>
                        <FieldLabel>Signature *</FieldLabel>
                        <p className="text-[11px] text-[#5E636E] -mt-1 mb-2">Sign with your mouse (or your finger on mobile). This is your legally binding signature.</p>
                        <SignaturePad ref={signatureRef} testId="enrol-authority-signature-pad" />
                        <div className="mt-2 flex items-center justify-between text-[12px]">
                            <button type="button"
                                onClick={() => signatureRef.current?.clear()}
                                className="text-[#5E636E] hover:text-[#0F1115]"
                                data-testid="enrol-authority-signature-clear"
                            >Clear signature</button>
                            <span className="text-[#B4B4BC]">Signed on {new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })}</span>
                        </div>
                    </div>

                    <label className="inline-flex items-start gap-2 text-[13px] text-[#0F1115]">
                        <input type="checkbox" checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            className="mt-0.5"
                            data-testid="enrol-authority-accept" />
                        <span>I accept the Authority to Act and Engagement Terms above.</span>
                    </label>

                    <div className="flex justify-between pt-2">
                        <button type="button" onClick={() => setStep("service")}
                            className="text-[13px] text-[#5E636E] hover:text-[#0F1115]"
                            data-testid="enrol-authority-back">← Back</button>
                        <button type="submit" disabled={busy || !acceptTerms}
                            className="rounded-full bg-[#0F1115] text-white px-6 py-2.5 text-[14px] font-medium hover:bg-[#1F2328] disabled:opacity-50"
                            data-testid="enrol-authority-next">
                            {busy ? "Signing…" : "Sign & finish enrolment →"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
